import * as React from "react";
import { Dialog, Popover, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  CubeTransparentIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  BookOpenIcon,
  NewspaperIcon,
  ChatBubbleLeftRightIcon,
  KeyIcon,
  CircleStackIcon,
  BuildingLibraryIcon,
  Squares2X2Icon,
  ArrowPathIcon,
  SparklesIcon,
  BanknotesIcon,
  GlobeAltIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom-v5-compat";
import { getEnvironmentVariables, setRedirectUrl } from "../../../common/utils";
import { useState, useEffect, useRef, Fragment } from "react";
import { Routes } from "../../../common/routing";
import WithOrganisations from "../../../components/Organisations/WithOrganisations";

const buildDocUrl = (docId) =>
  `${Routes.Website.Pages.Docs.Base}?id=${docId}`;

const platformSections = [
  {
    label: "Build",
    items: [
      {
        name: "Day 0: Provisioning",
        description: "Spin up environments, infrastructure and shared services in minutes.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.Day0Provisioning),
        icon: CloudArrowUpIcon,
      },
      {
        name: "The Workflow Engine",
        description: "Generators, pipelines and golden paths that automate every platform task.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.WorkflowEngine),
        icon: ArrowPathIcon,
      },
    ],
  },
  {
    label: "Run",
    items: [
      {
        name: "Day 1: Active Development",
        description: "Ship and iterate on production-grade APIs on AKS, day after day.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.Day1ActiveDevelopment),
        icon: CubeTransparentIcon,
      },
      {
        name: "Single Pane of Glass",
        description: "One unified view of every service, API and environment across your estate.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.SinglePaneOfGlass),
        icon: Squares2X2Icon,
      },
    ],
  },
  {
    label: "Govern",
    items: [
      {
        name: "Day 2: Per App Governance",
        description: "Policy and guardrails enforced for every workload you run.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.Day2PerAppGovernance),
        icon: ShieldCheckIcon,
      },
      {
        name: "Platform Governance",
        description: "Compliance, controls and audit baked into the platform itself.",
        href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.PlatformGovernance),
        icon: BuildingLibraryIcon,
      },
    ],
  },
];

const platformItems = platformSections.flatMap((s) => s.items);

const platformMobileItems = [
  {
    name: "Pellerex Overview",
    icon: SparklesIcon,
    href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.Overview),
  },
  ...platformItems,
];

const solutionsSections = [
  {
    label: "Build",
    items: [
      { name: "Platform-as-a-Service", description: "Managed platform capabilities delivered as a service.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.PaaS), icon: CpuChipIcon },
      { name: "Microservice Platforms", description: "Deploy, discover and govern microservices at scale.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.MicroservicePlatforms), icon: CircleStackIcon },
      { name: "API Monetisation", description: "Turn your APIs into products partners can build on.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.ApiMonetisation), icon: KeyIcon },
    ],
  },
  {
    label: "Modernise",
    items: [
      { name: "Digital Transformation", description: "Ship new digital products on a modern estate.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.DigitalTransformation), icon: SparklesIcon },
      { name: "Brownfield Platforms", description: "Modernise and govern complex existing estates.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.BrownfieldPlatforms), icon: WrenchScrewdriverIcon },
      { name: "Cloud Abstraction", description: "One consistent layer over AKS and shared services.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.CloudAbstraction), icon: CloudArrowUpIcon },
    ],
  },
  {
    label: "Operate & Govern",
    items: [
      { name: "Multi-Cloud Operations", description: "Consistent operations across AWS, Azure and GCP.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.MultiCloudOperations), icon: GlobeAltIcon },
      { name: "Regulated Industries", description: "Policy controls and audit-ready infrastructure.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.RegulatedIndustries), icon: ShieldCheckIcon },
      { name: "FinOps & Cost Governance", description: "Visibility and control over your cloud spend.", href: buildDocUrl(Routes.Website.Pages.Docs.PlatformWalkthrough.FinOps), icon: BanknotesIcon },
    ],
  },
];
const solutionsItems = solutionsSections.flatMap((s) => s.items);

const resourceItems = [
  {
    name: "Documentation",
    description: "Guides, references and how-tos for the platform.",
    href: Routes.Website.Pages.Docs.Base,
    icon: BookOpenIcon,
  },
  {
    name: "Blog",
    description: "Stories from the field and engineering deep dives.",
    href: Routes.Website.Pages.Blogs.Directory,
    icon: NewspaperIcon,
  },
  {
    name: "Talk to experts",
    description: "Book time with a Pellerex platform engineer.",
    href: Routes.Website.Pages.Conversion.TalkToExpert,
    icon: ChatBubbleLeftRightIcon,
  },
];

const MenuItem = ({ item }) => (
  <a
    href={item.href}
    className={`menuitem group relative flex flex-col gap-y-2 overflow-hidden rounded-xl ${item.compact ? "p-2" : "p-3 gap-y-2.5"} ring-1 ring-transparent transition-all duration-300 hover:bg-white/[0.04] hover:ring-white/10 hover:shadow-[0_18px_40px_-12px_rgba(163,230,53,0.45)]`}
  >
    {/* animated conic gradient border on hover */}
    <span
      aria-hidden="true"
      className="menuitem-border pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      style={{
        background:
          "conic-gradient(from var(--angle, 0deg), rgba(163,230,53,0.55), rgba(163,230,53,0.35), rgba(163,230,53,0.55))",
        padding: "1px",
        WebkitMask:
          "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
      }}
    />
    {/* shimmer sweep across the card on hover */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 45%, rgba(163,230,53,0.18) 50%, rgba(255,255,255,0.06) 55%, transparent 100%)",
      }}
    />
    {/* hover glow that animates from the icon outward */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -left-8 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
      style={{
        background:
          "radial-gradient(circle, rgba(163,230,53,0.45) 0%, rgba(163,230,53,0) 70%)",
      }}
    />
    {/* dot grid that fades in on hover */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-30"
      style={{
        backgroundImage:
          "radial-gradient(rgba(163,230,53,0.35) 1px, transparent 1px)",
        backgroundSize: "12px 12px",
        maskImage:
          "radial-gradient(circle at 20% 50%, rgba(0,0,0,0.7) 0%, transparent 60%)",
        WebkitMaskImage:
          "radial-gradient(circle at 20% 50%, rgba(0,0,0,0.7) 0%, transparent 60%)",
      }}
    />
    {/* bottom gradient hairline */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-3 bottom-0 h-px scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(163,230,53,0.6) 50%, transparent 100%)",
        transformOrigin: "left",
      }}
    />
    <div className="relative flex items-center gap-x-3">
      <div
        className={`relative flex ${item.compact ? "h-7 w-7" : "h-9 w-9"} flex-none items-center justify-center overflow-visible rounded-lg bg-white/5 ring-1 ring-white/10 transition-all duration-300 group-hover:bg-[#a3e635]/15 group-hover:ring-[#a3e635]/50 group-hover:shadow-[0_0_28px_rgba(163,230,53,0.55)]`}
      >
      {/* expanding pulse ring */}
      <span
        aria-hidden="true"
        className="menuitem-pulse pointer-events-none absolute inset-0 rounded-lg opacity-0 ring-1 ring-[#a3e635]/60 group-hover:opacity-100"
      />
      {/* icon container subtle inner glow on hover */}
      <span
        aria-hidden="true"
        className="absolute inset-0 overflow-hidden rounded-lg opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "linear-gradient(135deg, rgba(163,230,53,0.22) 0%, rgba(163,230,53,0.1) 100%)",
        }}
      />
      {/* corner spark */}
      <span
        aria-hidden="true"
        className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background: "#a3e635",
          boxShadow: "0 0 6px rgba(163,230,53,0.6)",
          animation: "menuitem-spark 1.6s ease-in-out infinite",
        }}
      />
      {/* orbiting sparks */}
      <span
        aria-hidden="true"
        className="menuitem-orbit-1 pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0"
        style={{
          background: "#a3e635",
          boxShadow: "0 0 8px rgba(163,230,53,0.95)",
        }}
      />
      <span
        aria-hidden="true"
        className="menuitem-orbit-2 pointer-events-none absolute left-1/2 top-1/2 h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0"
        style={{
          background: "#a3e635",
          boxShadow: "0 0 8px rgba(163,230,53,0.95)",
        }}
      />
      <item.icon
        className={`menuitem-icon relative ${item.compact ? "h-4 w-4" : "h-5 w-5"} text-white/80 transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-4deg] group-hover:text-[#a3e635]`}
        aria-hidden="true"
      />
      </div>
      <p
        className={`menuitem-title flex-1 min-w-0 ${item.compact ? "text-sm" : "text-base"} font-semibold leading-tight transition-transform duration-300 group-hover:translate-x-0.5`}
        style={{ color: "#ffffff" }}
      >
        {item.name}
        {item.badge && (
          <span
            className="ml-1.5 inline-flex items-center rounded-full border px-1.5 py-px align-middle text-[9px] font-bold uppercase tracking-wider"
            style={
              item.badge.tone === "green"
                ? {
                    borderColor: "rgba(163,230,53,0.4)",
                    background: "rgba(163,230,53,0.1)",
                    color: "#a3e635",
                  }
                : {
                    borderColor: "rgba(163,230,53,0.4)",
                    background: "rgba(163,230,53,0.1)",
                    color: "#a3e635",
                  }
            }
          >
            {item.badge.label}
          </span>
        )}
      </p>
      <ChevronRightIcon
        className="h-4 w-4 flex-none -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
        style={{ color: "#a3e635" }}
        aria-hidden="true"
      />
    </div>
    <p
      className={`relative leading-5 ${item.compact ? "text-xs line-clamp-1" : "text-sm line-clamp-2"}`}
      style={{ color: "rgba(255,255,255,0.65)" }}
    >
      {item.description}
    </p>
  </a>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-x-2 px-3 pt-2 pb-1">
    <span
      className="text-[11px] font-bold uppercase tracking-[0.18em]"
      style={{ color: "rgba(255,255,255,0.7)" }}
    >
      {children}
    </span>
    <span
      aria-hidden="true"
      className="h-px flex-1"
      style={{
        background:
          "linear-gradient(90deg, rgba(255,255,255,0.12) 0%, transparent 100%)",
      }}
    />
  </div>
);

const FeatureCard = ({ feature, illustration, side = "right", colSpanClass = "col-span-4" }) => (
  <a
    href={feature.href}
    className={`group relative ${colSpanClass} m-3 flex flex-col overflow-hidden rounded-xl p-6 ring-1 ring-white/10 transition-all duration-300 hover:ring-[#a3e635]/40 ${
      side === "right" ? "ml-0" : "mr-0"
    }`}
    style={{
      background:
        "linear-gradient(135deg, rgba(163,230,53,0.14) 0%, rgba(163,230,53,0.06) 60%, rgba(13,17,23,0.5) 100%)",
    }}
  >
    {/* dot grid pattern */}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-40"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)",
        backgroundSize: "14px 14px",
        maskImage:
          "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 70%)",
        WebkitMaskImage:
          "linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 70%)",
      }}
    />
    <div
      aria-hidden="true"
      className={`absolute -top-10 h-32 w-32 rounded-full bg-[#a3e635]/20 blur-3xl transition-all duration-500 group-hover:bg-[#a3e635]/30 ${
        side === "right" ? "-right-10" : "-left-10"
      }`}
    />
    {/* enlarged illustration fills the top space */}
    <div
      aria-hidden="true"
      className="relative flex flex-1 items-center justify-center"
    >
      {illustration}
    </div>
    {/* compact text block at the bottom */}
    <div className="relative mt-4">
      <span
        className="inline-flex w-fit items-center gap-x-1 rounded-full border border-[#a3e635]/40 bg-[#a3e635]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: "#a3e635" }}
      >
        {feature.tag}
      </span>
      <p
        className="mt-3 text-lg font-semibold leading-tight"
        style={{ color: "#ffffff" }}
      >
        {feature.title}
      </p>
      <p
        className="mt-2 text-sm leading-6"
        style={{ color: "rgba(255,255,255,0.75)" }}
      >
        {feature.description}
      </p>
      <span
        className="mt-4 inline-flex items-center gap-x-1 text-sm font-semibold"
        style={{ color: "#a3e635" }}
      >
        {feature.cta}
        <ChevronRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
      </span>
    </div>
  </a>
);

const MegaMenuPanel = ({
  items,
  sections,
  groups,
  columns = 2,
  featuredCount,
  footer,
  feature,
  leadingFeature,
  leadingIllustration,
  featureIllustration,
  width = "max-w-3xl",
  panelTop,
  open,
  onMouseEnter,
  onMouseLeave,
}) => (
  <Transition
    show={open}
    as={Fragment}
    enter="transition ease-out duration-200"
    enterFrom="opacity-0 translate-y-1"
    enterTo="opacity-100 translate-y-0"
    leave="transition ease-in duration-150"
    leaveFrom="opacity-100 translate-y-0"
    leaveTo="opacity-0 translate-y-1"
  >
    <div
      className={`fixed left-1/2 z-50 w-screen ${width} -translate-x-1/2 px-4 text-white`}
      style={{ top: panelTop }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* small bridge directly above panel for the final gap */}
      <div aria-hidden="true" className="absolute inset-x-0 -top-3 h-3" />
      <div className="relative overflow-hidden rounded-2xl bg-[#11161d] ring-1 ring-white/10 shadow-2xl shadow-black/60">
        {/* subtle top accent line */}
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(129,140,248,0.5) 50%, transparent 100%)",
          }}
          aria-hidden="true"
        />
        <div
          className={
            leadingFeature && feature
              ? "grid grid-cols-[2.5fr_7fr_2.5fr]"
              : feature || leadingFeature
              ? "grid grid-cols-12"
              : ""
          }
        >
          {leadingFeature && (
            <FeatureCard
              feature={leadingFeature}
              illustration={leadingIllustration}
              side="left"
              colSpanClass={feature ? "" : "col-span-4"}
            />
          )}
          <div
            className={
              leadingFeature && feature
                ? "p-5"
                : leadingFeature
                ? "col-span-8 p-5"
                : feature
                ? "col-span-8 p-5"
                : "p-5"
            }
          >
            {groups ? (
              <div className="flex flex-col gap-y-4">
                {groups.map((group, gi) => (
                  <div key={group.label} className={gi > 0 ? "border-t border-white/[0.07] pt-3" : ""}>
                    <SectionLabel>{group.label}</SectionLabel>
                    <div className={`mt-1 grid items-start ${group.featured ? "gap-4" : "gap-2"} grid-cols-2 md:grid-cols-4`}>
                      {group.items.map((item) => (
                        <MenuItem key={item.name} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : sections ? (
              (() => {
                const maxItems = Math.max(
                  ...sections.map((s) => s.items.length)
                );
                return (
                  <div
                    className={`grid gap-5 ${
                      sections.length >= 3
                        ? "grid-cols-1 md:grid-cols-3"
                        : "grid-cols-1 md:grid-cols-2"
                    }`}
                    style={{
                      gridTemplateRows: `auto repeat(${maxItems}, 1fr)`,
                    }}
                  >
                    {sections.map((section) => (
                      <div
                        key={section.label}
                        className="grid grid-rows-subgrid gap-5"
                        style={{ gridRow: `span ${maxItems + 1}` }}
                      >
                        <SectionLabel>{section.label}</SectionLabel>
                        {section.items.map((item) => (
                          <MenuItem key={item.name} item={item} />
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : featuredCount ? (
              <>
                <div
                  className={`grid items-start ${
                    columns === 4
                      ? "gap-4 grid-cols-2 md:grid-cols-4"
                      : columns === 3
                      ? "gap-5 grid-cols-1 md:grid-cols-3"
                      : "gap-5 grid-cols-1 md:grid-cols-2"
                  }`}
                >
                  {items.slice(0, featuredCount).map((item) => (
                    <MenuItem key={item.name} item={item} />
                  ))}
                </div>
                <div className="mt-1 grid grid-cols-2 gap-2 md:grid-cols-4 items-start border-t border-white/[0.07] pt-3">
                  {items.slice(featuredCount).map((item) => (
                    <MenuItem key={item.name} item={item} />
                  ))}
                </div>
              </>
            ) : (
              <div
                className={`grid items-start ${
                  columns === 4
                    ? "gap-3 grid-cols-2 md:grid-cols-4"
                    : columns === 3
                    ? "gap-5 grid-cols-1 md:grid-cols-3"
                    : "gap-5 grid-cols-1 md:grid-cols-2"
                }`}
              >
                {items.map((item) => (
                  <MenuItem key={item.name} item={item} />
                ))}
              </div>
            )}
          </div>
          {feature && (
            <FeatureCard
              feature={feature}
              illustration={featureIllustration}
              side="right"
              colSpanClass={leadingFeature ? "" : "col-span-4"}
            />
          )}
        </div>
        {footer && (
          <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.02] px-5 py-3 text-xs">
            {footer}
          </div>
        )}
      </div>
    </div>
  </Transition>
);

const navOpenSetters = new Set();

const NavPopover = ({ label, items, sections, groups, columns, featuredCount, footer, feature, leadingFeature, leadingIllustration, featureIllustration, width }) => {
  const buttonRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [panelTop, setPanelTop] = useState(0);
  const [open, setOpen] = useState(false);
  const closeImmediately = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(false);
  };
  useEffect(() => {
    navOpenSetters.add(closeImmediately);
    return () => {
      navOpenSetters.delete(closeImmediately);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updatePanelTop = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPanelTop(rect.bottom + 8);
  };
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const handleMouseEnter = () => {
    cancelClose();
    // Immediately close any other open nav menu so panels never overlap
    navOpenSetters.forEach((fn) => {
      if (fn !== closeImmediately) fn();
    });
    updatePanelTop();
    setOpen(true);
  };
  const handleMouseLeave = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };
  useEffect(() => () => cancelClose(), []);
  useEffect(() => {
    if (!open) return;
    const onResize = () => updatePanelTop();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize);
    };
  }, [open]);
  return (
  <div
    className="relative"
    onMouseEnter={handleMouseEnter}
    onMouseLeave={handleMouseLeave}
  >
        <button
          type="button"
          ref={buttonRef}
          className={`group relative inline-flex items-center gap-x-1 px-3 py-3 text-sm font-medium leading-6 outline-none transition-colors duration-200 ${
            open ? "text-white" : "text-white/80 hover:text-white"
          }`}
        >
          {label}
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform duration-200 ${
              open
                ? "rotate-180 text-white"
                : "text-white/60 group-hover:text-white"
            }`}
            aria-hidden="true"
          />
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute -bottom-1 left-1/2 h-0.5 -translate-x-1/2 rounded-full bg-[#818cf8] transition-all duration-300 ${
              open ? "w-6 opacity-100" : "w-0 opacity-0"
            }`}
          />
        </button>
        <MegaMenuPanel
          items={items}
          sections={sections}
          groups={groups}
          columns={columns}
          featuredCount={featuredCount}
          footer={footer}
          feature={feature}
          leadingFeature={leadingFeature}
          leadingIllustration={leadingIllustration}
          featureIllustration={featureIllustration}
          width={width}
          panelTop={panelTop}
          open={open}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
  </div>
  );
};

const PellerexOverviewIllustration = () => (
  <svg
    viewBox="10 4 220 120"
    preserveAspectRatio="xMidYMid meet"
    className="h-full w-full transition-transform duration-500 group-hover:scale-[1.02]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="plxEdge" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0" />
        <stop offset="50%" stopColor="#a3e635" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="plxPlatform" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#818cf8" stopOpacity="0.35" />
      </linearGradient>
      <radialGradient id="plxGlow" cx="0.5" cy="0.5" r="0.55">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* center platform glow */}
    <ellipse cx="120" cy="66" rx="110" ry="30" fill="url(#plxGlow)" />

    {/* top assets row: APIs, Apps, Agents, MCP */}
    {[
      { x: 37, label: "API" },
      { x: 91, label: "APP" },
      { x: 145, label: "AGENT" },
      { x: 199, label: "MCP" },
    ].map((node, i) => (
      <g key={`top-${i}`}>
        <rect x={node.x - 22} y="10" width="44" height="22" rx="3.5"
          fill="rgba(255,255,255,0.05)"
          stroke="rgba(255,255,255,0.28)" strokeWidth="1" />
        <text x={node.x} y="25" textAnchor="middle"
          fontSize="11" fontWeight="700" letterSpacing="0.6"
          fontFamily="ui-monospace, monospace"
          fill="rgba(255,255,255,0.92)">{node.label}</text>
        <path d={`M${node.x} 32 L${node.x} 50`} stroke="url(#plxEdge)" strokeWidth="1.4" />
      </g>
    ))}

    {/* center platform bar */}
    <rect x="14" y="50" width="212" height="32" rx="5"
      fill="url(#plxPlatform)"
      stroke="rgba(163,230,53,0.7)" strokeWidth="1.4" />
    <text x="120" y="71" textAnchor="middle"
      fontSize="14" fontWeight="700" letterSpacing="2.2"
      fontFamily="ui-sans-serif, system-ui"
      fill="#ffffff">PELLEREX PLATFORM</text>

    {/* bottom infra row: Cloud, Compute, Data */}
    {[
      { x: 46, label: "CLOUD" },
      { x: 118, label: "COMPUTE" },
      { x: 190, label: "DATA" },
    ].map((node, i) => (
      <g key={`bot-${i}`}>
        <path d={`M${node.x} 82 L${node.x} 98`} stroke="url(#plxEdge)" strokeWidth="1.4" />
        <rect x={node.x - 30} y="98" width="60" height="22" rx="3.5"
          fill="rgba(129,140,248,0.12)"
          stroke="rgba(129,140,248,0.45)" strokeWidth="1" />
        <text x={node.x} y="113" textAnchor="middle"
          fontSize="11" fontWeight="700" letterSpacing="0.8"
          fontFamily="ui-monospace, monospace"
          fill="rgba(255,255,255,0.9)">{node.label}</text>
      </g>
    ))}

    {/* moving pulse dots flowing top→platform→bottom */}
    <circle r="2" fill="#a3e635">
      <animateMotion dur="2.4s" repeatCount="indefinite"
        path="M37 32 L37 50" />
    </circle>
    <circle r="2" fill="#a3e635">
      <animateMotion dur="2.8s" repeatCount="indefinite"
        path="M145 32 L145 50" />
    </circle>
    <circle r="2" fill="#818cf8">
      <animateMotion dur="2.6s" repeatCount="indefinite"
        path="M46 82 L46 98" />
    </circle>
    <circle r="2" fill="#818cf8">
      <animateMotion dur="3s" repeatCount="indefinite"
        path="M190 82 L190 98" />
    </circle>
  </svg>
);

const McpGatewayIllustration = () => (
  <svg
    viewBox="10 8 220 104"
    preserveAspectRatio="xMidYMid meet"
    className="h-full w-full transition-transform duration-500 group-hover:scale-[1.02]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="mcpEdge" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0" />
        <stop offset="50%" stopColor="#a3e635" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="mcpHub" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#818cf8" stopOpacity="0.25" />
      </linearGradient>
      <radialGradient id="mcpGlow" cx="0.5" cy="0.5" r="0.55">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#a3e635" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="120" cy="60" r="56" fill="url(#mcpGlow)" />

    {/* spokes — left tags → hub */}
    <path d="M58 26 L88 60" stroke="url(#mcpEdge)" strokeWidth="1.4" />
    <path d="M58 60 L88 60" stroke="url(#mcpEdge)" strokeWidth="1.4" />
    <path d="M58 94 L88 60" stroke="url(#mcpEdge)" strokeWidth="1.4" />
    {/* spokes — hub → right partners */}
    <path d="M152 60 L186 26" stroke="url(#mcpEdge)" strokeWidth="1.4" />
    <path d="M152 60 L186 60" stroke="url(#mcpEdge)" strokeWidth="1.4" />
    <path d="M152 60 L186 94" stroke="url(#mcpEdge)" strokeWidth="1.4" />

    {/* left side: 3 API tags */}
    {[26, 60, 94].map((y, i) => (
      <g key={`l-${i}`}>
        <rect x="14" y={y - 12} width="44" height="24" rx="3.5"
          fill="rgba(255,255,255,0.05)"
          stroke="rgba(255,255,255,0.28)" strokeWidth="1" />
        <text x="36" y={y + 4} textAnchor="middle"
          fontSize="11" fontWeight="700" letterSpacing="0.6"
          fontFamily="ui-monospace, monospace"
          fill="rgba(255,255,255,0.92)">API</text>
      </g>
    ))}

    {/* right side: 3 partner faces */}
    {[26, 60, 94].map((y, i) => (
      <g key={`r-${i}`}>
        <circle cx="200" cy={y} r="13"
          fill="rgba(129,140,248,0.14)"
          stroke="rgba(129,140,248,0.55)" strokeWidth="1" />
        <circle cx="196" cy={y - 1.6} r="1.7" fill="#818cf8" />
        <circle cx="204" cy={y - 1.6} r="1.7" fill="#818cf8" />
        <path d={`M195 ${y + 4} Q200 ${y + 7} 205 ${y + 4}`}
          stroke="#818cf8" strokeWidth="1.1" fill="none" />
      </g>
    ))}

    {/* center hub: MCP Gateway */}
    <rect x="88" y="36" width="64" height="48" rx="7"
      fill="url(#mcpHub)"
      stroke="rgba(163,230,53,0.7)" strokeWidth="1.4" />
    <text x="120" y="58" textAnchor="middle"
      fontSize="14" fontWeight="700" letterSpacing="1"
      fontFamily="ui-sans-serif, system-ui"
      fill="#ffffff">MCP</text>
    <text x="120" y="75" textAnchor="middle"
      fontSize="9" letterSpacing="1.6" fontWeight="600"
      fontFamily="ui-sans-serif, system-ui"
      fill="rgba(255,255,255,0.85)">GATEWAY</text>

    {/* pulses along spokes */}
    <circle r="2" fill="#a3e635">
      <animateMotion dur="2.4s" repeatCount="indefinite"
        path="M58 26 L88 60" />
    </circle>
    <circle r="2" fill="#a3e635">
      <animateMotion dur="2.8s" repeatCount="indefinite"
        path="M58 94 L88 60" />
    </circle>
    <circle r="2" fill="#818cf8">
      <animateMotion dur="2.2s" repeatCount="indefinite"
        path="M152 60 L186 26" />
    </circle>
    <circle r="2" fill="#818cf8">
      <animateMotion dur="2.6s" repeatCount="indefinite"
        path="M152 60 L186 94" />
    </circle>
  </svg>
);

const PaaSCatalogIllustration = () => (
  <svg
    viewBox="10 4 220 240"
    preserveAspectRatio="xMidYMid meet"
    className="h-full w-full transition-transform duration-500 group-hover:scale-[1.02]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="paasHeader" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.55" />
        <stop offset="100%" stopColor="#818cf8" stopOpacity="0.4" />
      </linearGradient>
      <linearGradient id="paasFeatured" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.28" />
        <stop offset="100%" stopColor="#a3e635" stopOpacity="0.1" />
      </linearGradient>
      <filter id="paasBlur" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="24" />
      </filter>
    </defs>

    {/* soft gaussian-blurred backdrops */}
    <ellipse cx="120" cy="140" rx="80" ry="110" fill="#a3e635" fillOpacity="0.2" filter="url(#paasBlur)" />
    <ellipse cx="120" cy="26" rx="60" ry="18" fill="#a3e635" fillOpacity="0.2" filter="url(#paasBlur)" />

    {/* header strip: PELLEREX PAAS */}
    <rect x="20" y="10" width="200" height="32" rx="6"
      fill="url(#paasHeader)"
      stroke="rgba(163,230,53,0.78)" strokeWidth="1.4" />
    <text x="120" y="31" textAnchor="middle"
      fontSize="13" fontWeight="700" letterSpacing="2.4"
      fontFamily="ui-sans-serif, system-ui"
      fill="#ffffff">PELLEREX PAAS</text>

    {/* hairline divider */}
    <line x1="20" y1="50" x2="220" y2="50" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" />

    {/* entry 1: APIS — request/response arrows */}
    <rect x="20" y="58" width="200" height="32" rx="5"
      fill="rgba(255,255,255,0.03)"
      stroke="rgba(163,230,53,0.3)" strokeWidth="0.9" />
    <path d="M30 70 L46 70 M42 66 L46 70 L42 74" stroke="rgba(163,230,53,0.78)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
    <path d="M46 80 L30 80 M34 76 L30 80 L34 84" stroke="rgba(163,230,53,0.5)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
    <text x="60" y="79"
      fontSize="11" fontWeight="600" letterSpacing="1.4"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.85)">APIS</text>
    <path d="M198 68 L206 74 L198 80" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />

    {/* entry 2: APPS — 2x2 tile grid */}
    <rect x="20" y="96" width="200" height="32" rx="5"
      fill="rgba(255,255,255,0.03)"
      stroke="rgba(163,230,53,0.3)" strokeWidth="0.9" />
    <rect x="32" y="104" width="7" height="7" rx="1" fill="rgba(163,230,53,0.75)" />
    <rect x="41" y="104" width="7" height="7" rx="1" fill="rgba(163,230,53,0.5)" />
    <rect x="32" y="113" width="7" height="7" rx="1" fill="rgba(163,230,53,0.5)" />
    <rect x="41" y="113" width="7" height="7" rx="1" fill="rgba(163,230,53,0.75)" />
    <text x="60" y="117"
      fontSize="11" fontWeight="600" letterSpacing="1.4"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.85)">APPS</text>
    <path d="M198 106 L206 112 L198 118" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />

    {/* entry 3: AGENTS — featured, sparkle icon */}
    <rect x="20" y="134" width="200" height="32" rx="5"
      fill="url(#paasFeatured)"
      stroke="rgba(163,230,53,0.78)" strokeWidth="1.3" />
    <path d="M40 142 L42.5 148 L48.5 150 L42.5 152 L40 158 L37.5 152 L31.5 150 L37.5 148 Z"
      fill="rgba(163,230,53,0.95)" />
    <circle cx="49" cy="143" r="1.3" fill="rgba(163,230,53,0.7)" />
    <text x="60" y="155"
      fontSize="11" fontWeight="700" letterSpacing="1.4"
      fontFamily="ui-monospace, monospace"
      fill="#ffffff">AGENTS</text>
    <path d="M198 144 L206 150 L198 156" stroke="#a3e635" strokeWidth="1.4" fill="none" strokeLinejoin="round" />
    <circle cx="190" cy="150" r="2.4" fill="#a3e635">
      <animate attributeName="opacity" values="0.4;1;0.4" dur="2.2s" repeatCount="indefinite" />
    </circle>

    {/* entry 4: MCP SERVERS — hub + satellites */}
    <rect x="20" y="172" width="200" height="32" rx="5"
      fill="rgba(255,255,255,0.03)"
      stroke="rgba(163,230,53,0.3)" strokeWidth="0.9" />
    <path d="M40 188 L32 182 M40 188 L48 182 M40 188 L32 194 M40 188 L48 194" stroke="rgba(163,230,53,0.4)" strokeWidth="0.7" />
    <circle cx="32" cy="182" r="1.6" fill="rgba(163,230,53,0.55)" />
    <circle cx="48" cy="182" r="1.6" fill="rgba(163,230,53,0.55)" />
    <circle cx="32" cy="194" r="1.6" fill="rgba(163,230,53,0.55)" />
    <circle cx="48" cy="194" r="1.6" fill="rgba(163,230,53,0.55)" />
    <circle cx="40" cy="188" r="3.5" fill="rgba(163,230,53,0.8)" />
    <text x="60" y="193"
      fontSize="11" fontWeight="600" letterSpacing="1.4"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.85)">MCP SERVERS</text>
    <path d="M198 182 L206 188 L198 194" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />

    {/* entry 5: WORKFLOWS — connected nodes */}
    <rect x="20" y="210" width="200" height="32" rx="5"
      fill="rgba(255,255,255,0.03)"
      stroke="rgba(163,230,53,0.3)" strokeWidth="0.9" />
    <path d="M34 222 L44 226 M34 232 L44 228" stroke="rgba(163,230,53,0.45)" strokeWidth="0.8" />
    <circle cx="32" cy="220" r="2.6" fill="rgba(163,230,53,0.75)" />
    <circle cx="32" cy="232" r="2.6" fill="rgba(163,230,53,0.55)" />
    <circle cx="46" cy="226" r="2.6" fill="rgba(163,230,53,0.85)" />
    <text x="60" y="231"
      fontSize="11" fontWeight="600" letterSpacing="1.4"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.85)">WORKFLOWS</text>
    <path d="M198 220 L206 226 L198 232" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
  </svg>
);

const CloudAbstractionIllustration = () => (
  <svg
    viewBox="10 4 220 240"
    preserveAspectRatio="xMidYMid meet"
    className="h-full w-full transition-transform duration-500 group-hover:scale-[1.02]"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="caFlow" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.15" />
        <stop offset="50%" stopColor="#a3e635" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#818cf8" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="caCard" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a3e635" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#818cf8" stopOpacity="0.14" />
      </linearGradient>
      <filter id="caBlur" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="22" />
      </filter>
    </defs>

    {/* soft gaussian-blurred backdrops */}
    <ellipse cx="120" cy="132" rx="70" ry="60" fill="#a3e635" fillOpacity="0.26" filter="url(#caBlur)" />
    <ellipse cx="120" cy="220" rx="60" ry="18" fill="#818cf8" fillOpacity="0.18" filter="url(#caBlur)" />

    {/* top card: APP — the unified workload */}
    <rect x="40" y="14" width="160" height="50" rx="6"
      fill="rgba(255,255,255,0.04)"
      stroke="rgba(163,230,53,0.5)" strokeWidth="1.2" />
    <text x="120" y="40" textAnchor="middle"
      fontSize="16" fontWeight="700"
      fontFamily="ui-sans-serif, system-ui"
      fill="#ffffff">APP</text>
    <rect x="60" y="48" width="120" height="3" rx="1.5" fill="rgba(163,230,53,0.55)" />
    <rect x="60" y="55" width="80" height="3" rx="1.5" fill="rgba(163,230,53,0.3)" />

    {/* arrow down 1 */}
    <path d="M120 66 L120 92" stroke="url(#caFlow)" strokeWidth="2" />
    <path d="M114 86 L120 94 L126 86" stroke="#a3e635" strokeWidth="1.6" fill="none" strokeLinejoin="round" />

    {/* middle card: PELLEREX abstraction layer */}
    <rect x="22" y="92" width="196" height="80" rx="8"
      fill="url(#caCard)"
      stroke="rgba(163,230,53,0.8)" strokeWidth="1.6" />
    <text x="120" y="120" textAnchor="middle"
      fontSize="18" fontWeight="700" letterSpacing="0.6"
      fontFamily="ui-sans-serif, system-ui"
      fill="#ffffff">PELLEREX</text>
    <line x1="50" y1="130" x2="190" y2="130" stroke="rgba(255,255,255,0.18)" strokeWidth="0.8" />
    <text x="68" y="150" textAnchor="middle"
      fontSize="10" fontWeight="600" letterSpacing="1.5"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.78)">AKS</text>
    <text x="120" y="150" textAnchor="middle"
      fontSize="10" fontWeight="600" letterSpacing="1.5"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.78)">NET</text>
    <text x="172" y="150" textAnchor="middle"
      fontSize="10" fontWeight="600" letterSpacing="1.5"
      fontFamily="ui-monospace, monospace"
      fill="rgba(255,255,255,0.78)">IAM</text>
    <circle cx="68" cy="158" r="1.6" fill="#a3e635" />
    <circle cx="120" cy="158" r="1.6" fill="#a3e635" />
    <circle cx="172" cy="158" r="1.6" fill="#a3e635" />

    {/* arrow down 2 */}
    <path d="M120 174 L120 200" stroke="url(#caFlow)" strokeWidth="2" />
    <path d="M114 194 L120 202 L126 194" stroke="#818cf8" strokeWidth="1.6" fill="none" strokeLinejoin="round" />

    {/* bottom card: cloud providers */}
    <rect x="40" y="200" width="160" height="40" rx="6"
      fill="rgba(129,140,248,0.1)"
      stroke="rgba(129,140,248,0.5)" strokeWidth="1.2" />
    <text x="78" y="225" textAnchor="middle"
      fontSize="11" fontWeight="700" letterSpacing="1.2"
      fontFamily="ui-monospace, monospace"
      fill="#ffffff">AWS</text>
    <text x="120" y="225" textAnchor="middle"
      fontSize="11" fontWeight="700" letterSpacing="1.2"
      fontFamily="ui-monospace, monospace"
      fill="#ffffff">AZURE</text>
    <text x="162" y="225" textAnchor="middle"
      fontSize="11" fontWeight="700" letterSpacing="1.2"
      fontFamily="ui-monospace, monospace"
      fill="#ffffff">GCP</text>

    {/* flowing pulses top → bottom */}
    <circle r="2.4" fill="#a3e635">
      <animateMotion dur="2.4s" repeatCount="indefinite"
        path="M120 66 L120 92" />
    </circle>
    <circle r="2.4" fill="#818cf8">
      <animateMotion dur="2.4s" begin="1.2s" repeatCount="indefinite"
        path="M120 174 L120 200" />
    </circle>
  </svg>
);

const MarketingNavBar = (props) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [announcementVisible, setAnnouncementVisible] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(max > 0 ? Math.min(1, y / max) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logOut = () => {
    // Save current location so user can be redirected back after login
    setRedirectUrl({ pathname: window.location.href });
    props.logout({
      accessToken: props.accessToken,
      refreshToken: props.refreshToken,
    });
  };

  const simpleLinks = [
    { name: "Pricing", href: Routes.Website.Pages.Conversion.Prices },
  ];

  return (
    <div>
      <style>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @property --shine-x {
          syntax: '<percentage>';
          initial-value: 0%;
          inherits: false;
        }
        @keyframes menuitem-rotate-border {
          to { --angle: 360deg; }
        }
        @keyframes menuitem-spark {
          0%, 100% { transform: scale(0.8); opacity: 0.4; }
          50% { transform: scale(1.2); opacity: 0.7; }
        }
        @keyframes menuitem-spark-orbit {
          0% { transform: rotate(0deg) translateX(22px) rotate(0deg) scale(0.9); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: rotate(360deg) translateX(22px) rotate(-360deg) scale(0.9); opacity: 0; }
        }
        @keyframes menuitem-spark-orbit-2 {
          0% { transform: rotate(180deg) translateX(22px) rotate(-180deg) scale(0.7); opacity: 0; }
          15% { opacity: 0.9; }
          85% { opacity: 0.9; }
          100% { transform: rotate(540deg) translateX(22px) rotate(-540deg) scale(0.7); opacity: 0; }
        }
        @keyframes menuitem-icon-float {
          0%, 100% { transform: translateY(0) scale(1.1) rotate(-4deg); }
          50% { transform: translateY(-1px) scale(1.12) rotate(-6deg); }
        }
        @keyframes menuitem-title-glow {
          0%, 100% { text-shadow: 0 0 0 transparent; }
          50% { text-shadow: 0 0 12px rgba(129,140,248,0.5); }
        }
        @keyframes menuitem-pulse-ring {
          0% { transform: scale(0.85); opacity: 0.7; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes navbar-aurora-1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(40px, 8px) scale(1.1); }
          66% { transform: translate(-30px, -4px) scale(0.95); }
        }
        @keyframes navbar-aurora-2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-50px, 6px) scale(1.15); }
        }
        @keyframes navbar-shimmer {
          0% { background-position: 0% 0; }
          100% { background-position: 200% 0; }
        }
                .navbar-header nav { position: relative; z-index: 1; }
        .navbar-cta:hover .navbar-cta-border {
          animation: menuitem-rotate-border 3s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .navbar-cta:hover .navbar-cta-border { animation: none; }
        }
        .menuitem {
          transform-style: preserve-3d;
          perspective: 800px;
        }
        .menuitem:hover {
          transform: translateY(-2px) rotateX(2deg);
        }
        .menuitem:hover .menuitem-border {
          animation: menuitem-rotate-border 2.5s linear infinite;
        }
        .menuitem:hover .menuitem-icon {
          animation: menuitem-icon-float 2.4s ease-in-out infinite;
        }
        .menuitem:hover .menuitem-title {
          animation: menuitem-title-glow 2s ease-in-out infinite;
        }
        .menuitem:hover .menuitem-orbit-1 {
          animation: menuitem-spark-orbit 3s linear infinite;
        }
        .menuitem:hover .menuitem-orbit-2 {
          animation: menuitem-spark-orbit-2 3s linear infinite;
        }
        .menuitem:hover .menuitem-pulse {
          animation: menuitem-pulse-ring 1.8s ease-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .menuitem:hover { transform: none; }
          .menuitem:hover .menuitem-border,
          .menuitem:hover .menuitem-icon,
          .menuitem:hover .menuitem-title,
          .menuitem:hover .menuitem-orbit-1,
          .menuitem:hover .menuitem-orbit-2,
          .menuitem:hover .menuitem-pulse { animation: none; }
        }
      `}</style>
      <header
        className={`navbar-header sticky top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#0d1117]/85 backdrop-blur-xl border-b border-white/10"
            : "bg-[#0d1117] border-b border-white/10"
        }`}
      >
        {/* ambient effects layer (clipped to header bounds) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
        >
          {/* animated aurora blobs */}
          <div
            className="absolute -top-24 left-1/4 h-48 w-[28rem] rounded-full opacity-70 blur-3xl"
            style={{
              background:
                "radial-gradient(ellipse, rgba(129,140,248,0.28) 0%, rgba(129,140,248,0) 70%)",
              animation: "navbar-aurora-1 14s ease-in-out infinite",
            }}
          />
          <div
            className="absolute -top-20 right-1/4 h-40 w-[24rem] rounded-full opacity-60 blur-3xl"
            style={{
              background:
                "radial-gradient(ellipse, rgba(129,140,248,0.22) 0%, rgba(129,140,248,0) 70%)",
              animation: "navbar-aurora-2 18s ease-in-out infinite",
            }}
          />
          {/* faint grid pattern fades in on scroll */}
          <div
            className="absolute inset-0 transition-opacity duration-500"
            style={{
              opacity: scrolled ? 0.35 : 0.15,
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
              maskImage:
                "radial-gradient(ellipse 80% 100% at 50% 0%, rgba(0,0,0,0.6) 0%, transparent 100%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 80% 100% at 50% 0%, rgba(0,0,0,0.6) 0%, transparent 100%)",
            }}
          />
        </div>
        {/* scroll progress bar */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, #d9f99d 0%, #818cf8 50%, #d9f99d 100%)",
            backgroundSize: "200% 100%",
            transform: `scaleX(${scrollProgress})`,
            transformOrigin: "left",
            opacity: scrolled ? 0.9 : 0,
            transition:
              "transform 200ms ease-out, opacity 300ms ease",
            animation: scrolled ? "navbar-shimmer 3s linear infinite" : "none",
            boxShadow: scrolled ? "0 0 12px rgba(129,140,248,0.5)" : "none",
          }}
        />
        {/* Announcement bar */}
        {announcementVisible && (
          <div
            className="relative overflow-hidden border-b border-white/10"
            style={{
              background:
                "linear-gradient(90deg, rgba(163,230,53,0.08) 0%, rgba(163,230,53,0.06) 50%, rgba(163,230,53,0.08) 100%)",
            }}
          >
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-x-3 px-4 py-2 lg:px-8">
              <a
                href={buildDocUrl(
                  Routes.Website.Pages.Docs.ManagedApiService.McpIntegration
                )}
                className="group flex min-w-0 items-center gap-x-2 text-xs"
              >
                <span
                  className="inline-flex flex-none items-center rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                  style={{
                    borderColor: "rgba(163,230,53,0.45)",
                    background: "rgba(163,230,53,0.12)",
                    color: "#a3e635",
                  }}
                >
                  New
                </span>
                <span
                  className="truncate font-medium"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  MCP Gateway is now generally available — turn every API into
                  an AI-callable tool.
                </span>
                <span
                  className="hidden sm:inline-flex flex-none items-center gap-x-1 font-semibold transition-transform duration-200 group-hover:translate-x-0.5"
                  style={{ color: "#a3e635" }}
                >
                  Read more
                  <ChevronRightIcon className="h-3 w-3" />
                </span>
              </a>
              <button
                type="button"
                onClick={() => setAnnouncementVisible(false)}
                className="-m-1 flex-none rounded-md p-1 text-white/40 transition-colors hover:text-white/80"
                aria-label="Dismiss announcement"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        {/* Top hairline gradient (always visible at top edge) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(129,140,248,0.45) 30%, rgba(129,140,248,0.35) 70%, transparent 100%)",
            opacity: scrolled ? 1 : 0.5,
            transition: "opacity 300ms ease",
          }}
        />
        <nav
          className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 lg:px-8"
          aria-label="Global"
        >
          {/* Logo + wordmark */}
          <div className="flex lg:flex-1">
            <a
              href={`${getEnvironmentVariables().REACT_APP_WEBSITE_PUBLIC_URL}`}
              className="-m-1.5 flex items-center gap-x-2.5 p-1.5 group"
            >
              <span className="sr-only">Pellerex Pty. Ltd.</span>
              <img
                className="h-8 w-auto"
                src="https://cdn.pellerex.com/public/ecosystem/web/home/pellerex-logo.png"
                alt="Pellerex"
              />
              <span className="hidden sm:flex items-center gap-x-2">
                <span
                  className="text-base font-semibold tracking-tight"
                  style={{ color: "#ffffff" }}
                >
                  Pellerex
                </span>
              </span>
            </a>
          </div>

          {/* Mobile toggle */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white/80 hover:text-white"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Desktop links */}
          <Popover.Group className="hidden lg:flex lg:items-center lg:gap-x-8">
            <NavPopover
              label="Platform"
              sections={platformSections}
              width="max-w-7xl"
              leadingFeature={{
                tag: "What is Pellerex",
                title: "One platform for every workload",
                description:
                  "PaaS + IDP for APIs, apps, agents, and MCP servers on any cloud.",
                cta: "Read the overview",
                href: buildDocUrl(
                  Routes.Website.Pages.Docs.PlatformWalkthrough.Overview
                ),
              }}
              leadingIllustration={<PellerexOverviewIllustration />}
              feature={{
                tag: "What's new",
                title: "MCP Gateway is GA",
                description:
                  "Turn every API into an AI-callable tool over MCP.",
                cta: "Read the launch",
                href: buildDocUrl(
                  Routes.Website.Pages.Docs.ManagedApiService.McpIntegration
                ),
              }}
              featureIllustration={<McpGatewayIllustration />}
              footer={
                <>
                  <a
                    href={`${getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL}${Routes.Application.Identity.Organisation.Selection}`}
                    className="inline-flex items-center gap-x-1 font-medium transition-colors hover:text-[#818cf8]"
                    style={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    Explore the full platform
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={Routes.Website.Pages.Conversion.TalkToExpert}
                    className="inline-flex items-center gap-x-1 font-medium transition-colors hover:text-white"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    Talk to an architect →
                  </a>
                </>
              }
            />
            <NavPopover
              label="Solutions"
              sections={solutionsSections}
              width="max-w-7xl"
              leadingFeature={{
                tag: "Platform as a service",
                title: "Ship APIs, apps, agents and MCP servers",
                description:
                  "Every application primitive your team needs — delivered as one managed service.",
                cta: "Explore PaaS",
                href: buildDocUrl(
                  Routes.Website.Pages.Docs.PlatformWalkthrough.PaaS
                ),
              }}
              leadingIllustration={<PaaSCatalogIllustration />}
              feature={{
                tag: "One layer, every cloud",
                title: "Stop coding to AWS, Azure and GCP",
                description:
                  "Pellerex abstracts AKS, networking, identity and secrets into one consistent interface.",
                cta: "Explore cloud abstraction",
                href: buildDocUrl(
                  Routes.Website.Pages.Docs.PlatformWalkthrough.CloudAbstraction
                ),
              }}
              featureIllustration={<CloudAbstractionIllustration />}
              footer={
                <>
                  <a
                    href={buildDocUrl(
                      Routes.Website.Pages.Docs.PlatformWalkthrough.Overview
                    )}
                    className="inline-flex items-center gap-x-1 font-medium transition-colors hover:text-[#818cf8]"
                    style={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    Explore all solutions
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={Routes.Website.Pages.Conversion.TalkToExpert}
                    className="inline-flex items-center gap-x-1 font-medium transition-colors hover:text-white"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    Talk to an architect →
                  </a>
                </>
              }
            />
            <NavPopover label="Resources" items={resourceItems} columns={3} />
            {simpleLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="group relative inline-flex items-center px-1 py-1 text-sm font-medium leading-6 text-white/80 transition-colors duration-200 hover:text-white"
              >
                {item.name}
                <span
                  aria-hidden="true"
                  className="absolute -bottom-3 left-1/2 h-0.5 w-0 -translate-x-1/2 rounded-full bg-[#818cf8] transition-all duration-300 group-hover:w-6"
                />
              </a>
            ))}
          </Popover.Group>

          {/* Right CTAs */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-4">
            {!props.isAuthenticated && (
              <>
                <Link
                  to={`${getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL}${Routes.Application.Identity.Login}`}
                  className="text-sm font-medium leading-6 text-white/80 hover:text-white transition-colors duration-200"
                >
                  Sign in
                </Link>
                <a
                  href={Routes.Website.Pages.Conversion.TalkToExpert}
                  className="navbar-cta group relative inline-flex items-center gap-x-1.5 overflow-hidden rounded-full px-4 py-1.5 text-sm font-semibold text-[#0d1117] transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-[#818cf8]/40"
                  style={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="navbar-cta-border pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        "conic-gradient(from var(--angle, 0deg), rgba(129,140,248,0.9), rgba(129,140,248,0.7), rgba(129,140,248,0.9))",
                      padding: "1.5px",
                      WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 0%, rgba(129,140,248,0.25) 50%, transparent 100%)",
                    }}
                  />
                  <span className="relative">Talk to an expert</span>
                  <ChevronRightIcon className="relative h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </a>
              </>
            )}
            {props.isAuthenticated && (
              <>
                <button
                  onClick={logOut}
                  className="text-sm font-medium leading-6 text-white/80 hover:text-white transition-colors duration-200"
                >
                  Sign out
                </button>
                <Link
                  to={`${getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL}${Routes.Application.Identity.Organisation.Selection}`}
                  className="navbar-cta group relative inline-flex items-center gap-x-1.5 overflow-hidden rounded-full px-4 py-1.5 text-sm font-semibold text-[#0d1117] transition-all duration-300 shadow-sm hover:shadow-lg hover:shadow-[#818cf8]/40"
                  style={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="navbar-cta-border pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{
                      background:
                        "conic-gradient(from var(--angle, 0deg), rgba(129,140,248,0.9), rgba(129,140,248,0.7), rgba(129,140,248,0.9))",
                      padding: "1.5px",
                      WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -translate-x-full skew-x-[-20deg] opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent 0%, rgba(129,140,248,0.25) 50%, transparent 100%)",
                    }}
                  />
                  <span className="relative">Dashboard</span>
                  <ChevronRightIcon className="relative h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Scroll progress bar */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-0.5 origin-left transition-transform duration-150 ease-out"
          style={{
            transform: `scaleX(${scrollProgress})`,
            background:
              "linear-gradient(90deg, #818cf8 0%, #818cf8 100%)",
            opacity: scrollProgress > 0.001 ? 1 : 0,
          }}
        />

        <Dialog
          as="div"
          className="lg:hidden"
          open={mobileMenuOpen}
          onClose={setMobileMenuOpen}
        >
          <div className="fixed inset-0 z-50" />
          <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-[#0d1117] px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-white/10">
            <div className="flex items-center justify-between">
              <a
                href={`${getEnvironmentVariables().REACT_APP_WEBSITE_PUBLIC_URL}`}
                className="-m-1.5 p-1.5"
              >
                <span className="sr-only">Pellerex Pty. Ltd.</span>
                <img
                  className="h-8 w-auto"
                  src="https://cdn.pellerex.com/public/ecosystem/web/home/pellerex-logo.png"
                  alt="Pellerex"
                />
              </a>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-white/80 hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-white/10">
                <div className="space-y-1 py-6">
                  {[
                    { label: "Platform", items: platformMobileItems },
                    { label: "Solutions", items: solutionsItems },
                    { label: "Resources", items: resourceItems },
                  ].map((group) => (
                    <div key={group.label} className="py-3">
                      <p
                        className="mb-2 px-3 text-sm font-bold uppercase tracking-[0.2em]"
                        style={{ color: "#a3e635" }}
                      >
                        {group.label}
                      </p>
                      <div className="mt-1 space-y-1">
                        {group.items.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            className="-mx-1 flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium text-white/85 hover:bg-white/5 hover:text-white"
                          >
                            <item.icon className="h-5 w-5 text-white/60" />
                            {item.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                  <a
                    href={Routes.Website.Pages.Conversion.Prices}
                    className="-mx-1 mt-2 block rounded-lg px-3 py-2 text-sm font-semibold text-white hover:bg-white/5"
                  >
                    Pricing
                  </a>
                </div>
                <div className="py-6 space-y-2">
                  {!props.isAuthenticated && (
                    <>
                      <Link
                        to={`${getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL}${Routes.Application.Identity.Login}`}
                        className="-mx-1 block rounded-lg px-3 py-2.5 text-sm font-semibold text-white/85 hover:bg-white/5"
                      >
                        Sign in
                      </Link>
                      <a
                        href={Routes.Website.Pages.Conversion.TalkToExpert}
                        className="block rounded-full bg-white px-4 py-2 text-center text-sm font-semibold text-[#0d1117] hover:bg-white/90"
                      >
                        Talk to an expert
                      </a>
                    </>
                  )}
                  {props.isAuthenticated && (
                    <>
                      <Link
                        to={`${getEnvironmentVariables().REACT_APP_APPLICATION_PUBLIC_URL}${Routes.Application.Identity.Organisation.Selection}`}
                        className="block rounded-full bg-white px-4 py-2 text-center text-sm font-semibold text-[#0d1117] hover:bg-white/90"
                      >
                        Dashboard
                      </Link>
                      <button
                        type="button"
                        onClick={logOut}
                        className="-mx-1 block w-full rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-white/85 hover:bg-white/5"
                      >
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </Dialog>
      </header>
    </div>
  );
};

export default WithOrganisations(MarketingNavBar);